import { store$ } from '@redux/store';
import GoogleAnalyticsCustomDimensions from 'constants/googleAnalyticsCustomDimension';
import { 
  LocationType,
  selectListingCount,
  selectLocation,
  selectPage
} from 'features/searchResultsFacets';
import updateDataLayer from 'layout/components/dataLayer';
import { selectModal } from 'layout/redux/selectors';
import { distinctUntilChanged, filter, map, merge, skip } from 'rxjs';
import { getPageType } from 'services/googleAnalytics';
import cleanObjectsEmptyProperties from 'utils/cleanObject';
import UrlExtensions from 'utils/urlExtension';

/**
 * getEventForLocationChange: function to get an object for location change event
 * @param {object} location location object
 * @returns an object with properties for location change event
 */
const getEventForLocationChange = (location) => {
  let city = null;
  let state = null;
  if (location.locationType !== LocationType.RectangularArea.toString()) {
    city = location.city || '';
  }

  if (location.locationType === LocationType.State.toString()) {
    state = location.geoLocationState || undefined;
  }    

  return {
    event: 'location_change',
    [GoogleAnalyticsCustomDimensions.MarketId().getName()]: location.marketId,
    [GoogleAnalyticsCustomDimensions.City().getName()]: city || undefined,
    [GoogleAnalyticsCustomDimensions.MarketName().getName()]: location.marketName && location.marketState 
      ? `${location.marketName}, ${location.marketState}` 
      : undefined,
    [GoogleAnalyticsCustomDimensions.Location().getName()]: location.city
      ? `${location.city} ${location.marketStateName}`
      : location.marketStateName,
    [GoogleAnalyticsCustomDimensions.State().getName()]: state,
  };
};

/**
 * Subscribing to the store.
 * When the location leaf change, map the object to city, location, marketName and marketId custom dimensions.
 * Skip the first time for inital load.
 * Send the event location_change to notify tags in tag maanager when location change
 */
const locationChanged$ = store$.pipe(
  map(selectLocation),
  filter(Boolean),
  distinctUntilChanged(),
  skip(1),
  map(getEventForLocationChange),
  map(cleanObjectsEmptyProperties)
);

/**
 * Subscribing to the store.
 * When the page property change, map the object to currentPageNumber custom dimensions.
 * Skip the first time for inital load.
 * Send the event pagination_change to notify tags in tag maanager when pagination change
 */
const paginationChanged$ = store$.pipe(
  map(selectPage),
  filter(Boolean),
  distinctUntilChanged(),
  skip(1),
  map((page) => ({
    event: 'pagination_change',
    [GoogleAnalyticsCustomDimensions.CurrentPageNumber().getName()]: page,
  }))
);

/**
 * Subscribing to the store.
 * When the results type change, map the the selector to get the page type custom dimension.
 * Skip the first time for inital load.
 * Send the event product_change to notify tags in tag maanager when hosuing change
 */
const housingTypeChanged$ = store$.pipe(
  map((state) => state?.routingState?.currentUrl),
  filter(Boolean),
  distinctUntilChanged(),
  map(UrlExtensions.getSubdirectory),
  distinctUntilChanged(),
  map((page) => ({
    event: 'product_change',
    [GoogleAnalyticsCustomDimensions.PageType().getName()]: getPageType(page),
  }))
);

/**
 * Subscribing to the store.
 * When the counts change, map the the selector to get the page type custom dimension.
 * Skip the first time for inital load.
 */
const countsChanged$ = store$.pipe(
  map(selectListingCount),
  distinctUntilChanged(),
  skip(1),
  map((listingCount) => ({
    event: 'results_change',
    [GoogleAnalyticsCustomDimensions.MatchingResults().getName()]: listingCount,
  }))
);

/**
 * Subscribing to the store.
 * When the counties change, map the the selector to get the counties custom dimension.
 */
const leadModalOpened$ = store$.pipe(
  map(selectModal),
  distinctUntilChanged(),
  filter((modal) => modal?.name === 'leadGenerator'),
  map((modal) => ({
    [GoogleAnalyticsCustomDimensions.PriceReduction().getName()]: modal.listingInformation?.priceReductionLabel,
    [GoogleAnalyticsCustomDimensions.CommunityName().getName()]: modal.listingInformation?.communityName,
    [GoogleAnalyticsCustomDimensions.BrandName().getName()]: modal.listingInformation?.brandName,
    [GoogleAnalyticsCustomDimensions.SpecId().getName()]: modal.listingInformation?.specId,
    [GoogleAnalyticsCustomDimensions.PlanId().getName()]: modal.listingInformation?.planId,
    [GoogleAnalyticsCustomDimensions.CommunityId().getName()]: modal.listingInformation?.communityId,
    [GoogleAnalyticsCustomDimensions.IsPaid().getName()]: modal.listingInformation?.isBasic !== "true",
  }))
);

/**
 * Subscribing to the store.
 * When the counties change, map the the selector to get the counties custom dimension.
 */
const leadModalClosed$ = store$.pipe(
  map(selectModal),
  distinctUntilChanged(),
  filter((modal) => modal === null),
  map(() => ({
    [GoogleAnalyticsCustomDimensions.PriceReduction().getName()]: null,
    [GoogleAnalyticsCustomDimensions.CommunityName().getName()]: null,
    [GoogleAnalyticsCustomDimensions.BrandName().getName()]: null,
    [GoogleAnalyticsCustomDimensions.SpecId().getName()]: null,
    [GoogleAnalyticsCustomDimensions.PlanId().getName()]: null,
    [GoogleAnalyticsCustomDimensions.CommunityId().getName()]: null,
    [GoogleAnalyticsCustomDimensions.IsPaid().getName()]: null,
  }))
);

merge(
  countsChanged$,
  housingTypeChanged$,
  locationChanged$,
  paginationChanged$,
  leadModalOpened$,
  leadModalClosed$
  ).subscribe(
  updateDataLayer
);
